import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { Zap, Play, Pause, Plus, Trash2, Sparkles, Loader2, RefreshCw, ChevronDown, ChevronUp, FileText, Scale, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '../components/ui/Card';
import { PageLayout } from '@/components/layout/PageLayout';
import { tabService, type Workflow } from '../services/tabs';
import { api, handleApiError } from '../services/client';

interface SuggestionRule {
  id: string;
  name: string;
  enabled: boolean;
  condition: string;
}

interface DashboardAction {
  id: string;
  name: string;
  description: string;
  trigger: string;
  actions: string[];
  status: 'active' | 'paused';
  triggerCount: number;
  lastTriggered: Date | null;
  efficiency: number;
  source: 'workflow' | 'suggestion';
  suggestionType?: string;
  actionLabel?: string;
  tabCount?: number;
}

function safeDate(value: unknown): Date | null {
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
}

function normTitle(title: string): string {
  return title.trim().toLowerCase();
}

interface HistoryStats {
  count: number;
  lastAt: Date | null;
}

interface HistoryIndex {
  byId: Map<string, HistoryStats>;
  byTitle: Map<string, HistoryStats>;
  byWorkflowId: Map<string, HistoryStats>;
}


function buildHistoryIndex(history: unknown[]): HistoryIndex {
  const byId = new Map<string, HistoryStats>();
  const byTitle = new Map<string, HistoryStats>();
  const byWorkflowId = new Map<string, HistoryStats>();

  const bump = (map: Map<string, HistoryStats>, key: string, executedAt: Date | null) => {
    if (!key) return;
    const prev = map.get(key) ?? { count: 0, lastAt: null };
    map.set(key, {
      count: prev.count + 1,
      lastAt: executedAt && (!prev.lastAt || executedAt > prev.lastAt) ? executedAt : prev.lastAt,
    });
  };

  for (const row of history) {
    if (!row || typeof row !== 'object') continue;
    const r = row as Record<string, unknown>;
    const executedAt = safeDate(r.executed_at ?? r.executedAt);
    const id = String(r.id ?? '');
    const title = typeof r.title === 'string' ? normTitle(r.title) : '';
    const workflowId = String(r.workflow_id ?? r.workflowId ?? '');

    bump(byId, id, executedAt);
    bump(byTitle, title, executedAt);
    bump(byWorkflowId, workflowId, executedAt);
  }

  return { byId, byTitle, byWorkflowId };
}

function lookupTriggerStats(
  index: HistoryIndex,
  opts: { id: string; title: string; workflowId?: string },
): HistoryStats {
  const candidates: HistoryStats[] = [];
  const byId = index.byId.get(opts.id);
  if (byId) candidates.push(byId);
  const byTitle = index.byTitle.get(normTitle(opts.title));
  if (byTitle) candidates.push(byTitle);
  if (opts.workflowId) {
    const byWorkflow = index.byWorkflowId.get(opts.workflowId);
    if (byWorkflow) candidates.push(byWorkflow);
  }
  return candidates.reduce<HistoryStats>(
    (best, cur) => ({
      count: Math.max(best.count, cur.count),
      lastAt: cur.lastAt && (!best.lastAt || cur.lastAt > best.lastAt) ? cur.lastAt : best.lastAt,
    }),
    { count: 0, lastAt: null },
  );
}

function mapWorkflow(w: Workflow, index: HistoryIndex, historySuccessRate: number): DashboardAction {
  const hist = lookupTriggerStats(index, { id: w.id, title: w.name, workflowId: w.id });
  const dbRuns = typeof w.runs === 'number' ? w.runs : 0;
  const lastFromDb = w.lastRun ? safeDate(w.lastRun) : null;
  return {
    id: w.id,
    name: w.name || 'Workflow',
    description: w.description || '',
    trigger: w.trigger || 'Manual',
    actions: Array.isArray(w.actions) ? w.actions : [],
    status: w.status === 'paused' ? 'paused' : 'active',
    triggerCount: Math.max(dbRuns, hist.count),
    lastTriggered: lastFromDb ?? hist.lastAt,
    efficiency: historySuccessRate,
    source: 'workflow',
    actionLabel: 'Run',
  };
}

function mapSuggestion(
  raw: Record<string, unknown>,
  index: HistoryIndex,
  historyEfficiency: number,
): DashboardAction {
  const id = String(raw.id ?? crypto.randomUUID());
  const name = String(raw.title ?? raw.name ?? 'Suggestion');
  const hist = lookupTriggerStats(index, { id, title: name });
  const confidence = Number(raw.confidence ?? 85);

  // Prefer real measured efficiency (success rate from history). Fall back to
  // the model's confidence score when we have no history for this action.
  const efficiency = hist.count > 0 && historyEfficiency > 0
    ? historyEfficiency
    : Math.min(100, Math.max(1, Math.round(confidence)));

  const stype = String(raw.type ?? 'workflow');
  const tabIDs = Array.isArray(raw.tab_ids) ? raw.tab_ids : [];
  const tabCount = tabIDs.length;

  let computedLabel = 'Apply';
  if (stype === 'synthesize_archive') {
    computedLabel = 'Synthesize & Stash';
  } else if (stype === 'compare_decide') {
    computedLabel = 'Build Matrix';
  } else if (stype === 'convert') {
    computedLabel = 'Create GenTab';
  } else if (stype === 'duplicate_check') {
    computedLabel = 'Close Duplicates';
  }

  const actionLabel = typeof raw.actionLabel === 'string'
    ? raw.actionLabel
    : typeof raw.action_label === 'string'
      ? raw.action_label
      : computedLabel;

  return {
    id,
    name,
    description: String(raw.description ?? raw.reason ?? ''),
    trigger: String(raw.reason ?? raw.type ?? 'Context'),
    actions: [actionLabel],
    status: 'active',
    triggerCount: hist.count,
    lastTriggered: hist.lastAt,
    efficiency,
    source: 'suggestion',
    suggestionType: stype,
    actionLabel,
    tabCount,
  };
}

async function loadDashboardData(): Promise<{
  actions: DashboardAction[];
  rules: SuggestionRule[];
}> {
  const [workflows, suggestionsRes, history] = await Promise.all([
    tabService.getWorkflows().catch(() => [] as Workflow[]),
    api.get<{ suggestions?: Record<string, unknown>[] }>('/smart-actions/suggestions').catch(() => ({ data: { suggestions: [] } })),
    tabService.getSmartActionHistory().catch(() => []),
  ]);

  const historyIndex = buildHistoryIndex(history);
  // Real efficiency = fraction of history rows marked success=true, falling
  // back to the model's confidence when the user hasn't run the action yet.
  const successCount = history.filter((row) => {
    if (!row || typeof row !== 'object') return false;
    const r = row as Record<string, unknown>;
    return r.success === true || r.success === 'true' || r.success === 1;
  }).length;
  const historyEfficiency = history.length > 0
    ? Math.round((successCount / history.length) * 100)
    : 0;
  const suggestions = (suggestionsRes.data?.suggestions ?? []).map((item) =>
    mapSuggestion(item, historyIndex, historyEfficiency),
  );
  const workflowActions = workflows.map((w) => mapWorkflow(w, historyIndex, historyEfficiency));

  const rulesRes = await api.get<{ rules: SuggestionRule[] }>('/smart-actions/rules').catch(() => ({ data: { rules: [] } }));

  return {
    actions: [...workflowActions, ...suggestions],
    rules: rulesRes.data?.rules ?? [],
  };
}

const TRIGGER_OPTIONS = ['Manual', 'Time interval', 'On event', 'Tab change'] as const;
const ACTION_OPTIONS = ['Save session', 'Send notification', 'Create group', 'Group tabs', 'Close duplicates'] as const;

const SmartActionsDashboard: React.FC = () => {
  const [showCreate, setShowCreate] = useState(false);
  const [actions, setActions] = useState<DashboardAction[]>([]);
  const [rules, setRules] = useState<SuggestionRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [executingId, setExecutingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [createName, setCreateName] = useState('');
  const [createTrigger, setCreateTrigger] = useState<string>(TRIGGER_OPTIONS[0]);
  const [createCondition, setCreateCondition] = useState('');
  const [createAction, setCreateAction] = useState<string>(ACTION_OPTIONS[0]);
  const [creating, setCreating] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await loadDashboardData();
      setActions(data.actions);
      setRules(data.rules);
    } catch (err) {
      console.error(err);
      setLoadError(handleApiError(err));
      setActions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const stats = useMemo(() => {
    const active = actions.filter((a) => a.status === 'active').length;
    const triggers = actions.reduce((sum, a) => sum + a.triggerCount, 0);
    const avgEfficiency = actions.length > 0
      ? Math.round(actions.reduce((s, a) => s + a.efficiency, 0) / actions.length)
      : null;
    return { active, triggers, avgEfficiency, listed: actions.length };
  }, [actions]);

  const resetCreateForm = () => {
    setCreateName('');
    setCreateTrigger(TRIGGER_OPTIONS[0]);
    setCreateCondition('');
    setCreateAction(ACTION_OPTIONS[0]);
  };

  const handleCreate = async () => {
    const name = createName.trim();
    if (!name) {
      toast.error('Enter an action name');
      return;
    }
    setCreating(true);
    try {
      await tabService.createWorkflow({
        name,
        description: createCondition.trim() || undefined,
        trigger: createTrigger,
        actions: [createAction],
        steps: [{ action: createAction, target: createCondition.trim() || undefined }],
      });
      toast.success('Smart action created');
      setShowCreate(false);
      resetCreateForm();
      await refresh();
    } catch (err) {
      toast.error(handleApiError(err));
    } finally {
      setCreating(false);
    }
  };

  const recordExecution = async (action: DashboardAction) => {
    await tabService.recordSmartActionExecution({
      action_id: action.id,
      title: action.name,
      type: action.suggestionType ?? 'workflow',
      workflow_id: action.source === 'workflow' ? action.id : undefined,
    });
  };

  const executeSuggestion = async (action: DashboardAction) => {
    setExecutingId(action.id);
    try {
      // Server-side dispatcher does the real work now. We just hit
      // POST /smart-actions/execute and show whatever Details string the
      // executor returns — that string includes real tab titles, group names,
      // bookmarked URLs etc. (previously this was a switch that routed to
      // /skills/* and only worked for 3 of the 5 actions).
      const res = await tabService.executeSmartAction(action.id);
      const details = (res && typeof res === 'object' && 'result' in res
        ? (res as { result?: { details?: string } }).result?.details
        : undefined) ?? `Executed "${action.name}"`;
      toast.success(details);
      await refresh();
    } catch (err) {
      toast.error(handleApiError(err));
    } finally {
      setExecutingId(null);
    }
  };

  // LLM enrichment of suggestion Title + Reason. Runs after the page loads
  // so the user sees generic-but-correct cards immediately, then watches them
  // get personalized in place. Uses a per-component "enrichment in flight"
  // ref so a second refresh doesn't fire two parallel batches.
  const enrichmentInFlight = useRef(false);
  const enrichSuggestions = useCallback(async (items: DashboardAction[]) => {
    if (items.length === 0) return;
    if (enrichmentInFlight.current) return;
    enrichmentInFlight.current = true;
    try {
      const payload = {
        suggestions: items.map((a) => ({
          id: a.id,
          type: a.suggestionType ?? 'workflow',
          title: a.name,
          description: a.description,
        })),
      };
      const res = await api.post<{ suggestions?: Array<{ id: string; title?: string; reason?: string }> }>(
        '/smart-actions/enrich',
        payload,
      );
      const enriched = res.data?.suggestions ?? [];
      if (enriched.length === 0) return;
      const byId = new Map(enriched.map((s) => [s.id, s]));
      setActions((prev) =>
        prev.map((a) => {
          const hit = byId.get(a.id);
          if (!hit) return a;
          return {
            ...a,
            name: hit.title ?? a.name,
            description: hit.reason ?? a.description,
          };
        }),
      );
    } catch {
      // Enrichment is best-effort — silent failure is fine, the static text
      // is already on screen.
    } finally {
      enrichmentInFlight.current = false;
    }
  }, []);

  // Subscribe to NATS-driven smart_action_available notifications so the user
  // sees a toast the moment a new high-confidence suggestion appears, even
  // while the page is open. Reconnects with backoff on drop.
  useEffect(() => {
    let stopped = false;
    let backoff = 1000;
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = window.location.hostname;
    const wsPort = window.location.port || '3000';
    let socket: WebSocket | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const connect = () => {
      if (stopped) return;
      try {
        socket = new WebSocket(`${wsProtocol}//${wsHost}:${wsPort}/api/v1/ws`);
      } catch {
        timer = setTimeout(connect, backoff);
        backoff = Math.min(backoff * 2, 30000);
        return;
      }
      socket.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (data?.type === 'smart_action_available') {
            toast.info(data.title ?? 'New smart action available', {
              description: data.reason,
            });
            void refresh();
          }
        } catch {
          // ignore malformed frames
        }
      };
      socket.onclose = () => {
        if (stopped) return;
        timer = setTimeout(connect, backoff);
        backoff = Math.min(backoff * 2, 30000);
      };
      socket.onerror = () => {
        socket?.close();
      };
    };

    connect();
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      socket?.close();
    };
  }, [refresh]);

  // Kick off LLM enrichment whenever the suggestion list changes.
  const lastEnrichedKey = useRef('');
  useEffect(() => {
    const suggestions = actions.filter((a) => a.source === 'suggestion');
    if (suggestions.length === 0) return;
    const key = suggestions.map((s) => s.id).sort().join(',');
    if (key === lastEnrichedKey.current) return;
    lastEnrichedKey.current = key;
    void enrichSuggestions(suggestions);
  }, [actions, enrichSuggestions]);

  const executeWorkflow = async (action: DashboardAction) => {
    setExecutingId(action.id);
    try {
      await tabService.executeWorkflow(action.id);
      toast.success(`"${action.name}" ran successfully`);
      await refresh();
    } catch (err) {
      toast.error(handleApiError(err));
    } finally {
      setExecutingId(null);
    }
  };

  const toggleWorkflowStatus = async (action: DashboardAction) => {
    const next = action.status === 'active' ? 'paused' : 'active';
    try {
      await tabService.updateWorkflowStatus(action.id, next);
      setActions((prev) => prev.map((a) => (a.id === action.id ? { ...a, status: next } : a)));
      toast.success(next === 'active' ? 'Action resumed' : 'Action paused');
    } catch (err) {
      toast.error(handleApiError(err));
    }
  };

  const deleteWorkflow = async (action: DashboardAction) => {
    try {
      await tabService.deleteWorkflow(action.id);
      setActions((prev) => prev.filter((a) => a.id !== action.id));
      toast.success('Action deleted');
    } catch (err) {
      toast.error(handleApiError(err));
    }
  };

  const dismissSuggestion = (id: string) => {
    setActions((prev) => prev.filter((a) => a.id !== id));
  };

  const toggleRule = async (rule: SuggestionRule) => {
    const next = !rule.enabled;
    setRules((prev) => prev.map((r) => (r.id === rule.id ? { ...r, enabled: next } : r)));
    try {
      await api.put(`/smart-actions/rules/${rule.id}`, { enabled: next });
      await refresh();
      toast.success(next ? 'Rule enabled' : 'Rule disabled');
    } catch (err) {
      setRules((prev) => prev.map((r) => (r.id === rule.id ? { ...r, enabled: rule.enabled } : r)));
      toast.error(handleApiError(err));
    }
  };

  const formatLastTriggered = (date: Date | null) => {
    if (!date) return 'Never';
    return date.toLocaleTimeString();
  };

  return (
    <PageLayout title="Smart Actions Dashboard" description="Create and manage automated tab and browser actions" icon={Zap}>
      <div className="mb-8 flex items-center justify-between gap-4">
        <button
          onClick={() => {
            resetCreateForm();
            setShowCreate(!showCreate);
          }}
          className="flex items-center space-x-2 px-6 py-3 bg-primary-500 text-white rounded-xl font-medium shadow-lg hover:bg-primary-600 transition-colors"
        >
          <Plus className="h-5 w-5" />
          <span>New Action</span>
        </button>
        <button
          onClick={() => void refresh()}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-khiimori-border text-khiimori-text-secondary hover:bg-khiimori-bg-elevated transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {loadError && (
        <div className="mb-6 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          {loadError}
        </div>
      )}

      {showCreate && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-8">
          <Card className="bg-gradient-glass border-primary-400/30">
            <h3 className="font-bold text-khiimori-text-primary mb-4">Create Smart Action</h3>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Action name"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                className="w-full px-4 py-2 bg-khiimori-bg-elevated rounded-lg border border-khiimori-border text-khiimori-text-primary"
              />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-khiimori-text-secondary mb-2 block">Trigger</label>
                  <select
                    value={createTrigger}
                    onChange={(e) => setCreateTrigger(e.target.value)}
                    className="w-full px-4 py-2 bg-khiimori-bg-elevated rounded-lg border border-khiimori-border text-khiimori-text-primary"
                  >
                    {TRIGGER_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-khiimori-text-secondary mb-2 block">Condition</label>
                  <input
                    type="text"
                    placeholder="Add condition"
                    value={createCondition}
                    onChange={(e) => setCreateCondition(e.target.value)}
                    className="w-full px-4 py-2 bg-khiimori-bg-elevated rounded-lg border border-khiimori-border text-khiimori-text-primary"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-khiimori-text-secondary mb-2 block">Action</label>
                  <select
                    value={createAction}
                    onChange={(e) => setCreateAction(e.target.value)}
                    className="w-full px-4 py-2 bg-khiimori-bg-elevated rounded-lg border border-khiimori-border text-khiimori-text-primary"
                  >
                    {ACTION_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => { setShowCreate(false); resetCreateForm(); }}
                  className="px-4 py-2 bg-khiimori-bg-tertiary text-khiimori-text-secondary rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={() => void handleCreate()}
                  disabled={creating}
                  className="px-6 py-2 bg-primary-500 text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
                >
                  {creating && <Loader2 className="h-4 w-4 animate-spin" />}
                  Create
                </button>
              </div>
            </div>
          </Card>
        </motion.div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Active Actions', value: stats.active, textClass: 'text-primary-500' },
          { label: 'Total Triggers', value: stats.triggers, textClass: 'text-khiimori-accent-fuchsia' },
          { label: 'Avg Efficiency', value: stats.avgEfficiency != null ? `${stats.avgEfficiency}%` : '—', textClass: 'text-success-500' },
          { label: 'Listed', value: stats.listed, textClass: 'text-info-500' },
        ].map((stat, index) => (
          <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }}>
            <Card hoverable>
              <div className="text-center">
                <div className={`text-3xl font-bold ${stat.textClass}`}>{stat.value}</div>
                <div className="text-sm text-khiimori-text-secondary">{stat.label}</div>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-khiimori-text-muted">
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading actions…
        </div>
      ) : actions.length === 0 ? (
        <Card className="py-12 text-center">
          <Zap className="mx-auto mb-3 h-10 w-10 text-khiimori-text-muted" />
          <h3 className="text-lg font-semibold text-khiimori-text-primary">No smart actions yet</h3>
          <p className="mt-2 text-sm text-khiimori-text-muted">
            Create a workflow or open tabs in Live so AI can suggest actions.
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {actions.map((action, index) => (
            <motion.div key={`${action.source}-${action.id}`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}>
              <Card hoverable>
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex items-start space-x-4 flex-1 min-w-0">
                    <div className={`p-3 rounded-xl shrink-0 ${
                      action.suggestionType === 'synthesize_archive'
                        ? 'bg-gradient-to-br from-violet-500 to-indigo-600'
                        : action.suggestionType === 'compare_decide'
                        ? 'bg-gradient-to-br from-cyan-500 to-blue-600'
                        : action.status === 'active'
                        ? 'bg-primary-500'
                        : 'bg-khiimori-bg-tertiary'
                    }`}>
                      {action.suggestionType === 'synthesize_archive' ? (
                        <FileText className="h-6 w-6 text-white" />
                      ) : action.suggestionType === 'compare_decide' ? (
                        <Scale className="h-6 w-6 text-white" />
                      ) : (
                        <Zap className="h-6 w-6 text-khiimori-text-primary" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <h3 className="font-bold text-khiimori-text-primary">{action.name}</h3>
                        <span className={`px-3 py-0.5 rounded-full text-xs font-medium ${action.status === 'active' ? 'bg-success-500/20 text-success-500' : 'bg-khiimori-bg-tertiary text-khiimori-text-secondary'}`}>
                          {action.status}
                        </span>
                        {action.source === 'suggestion' && (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            action.suggestionType === 'synthesize_archive'
                              ? 'bg-violet-500/20 text-violet-300 border border-violet-500/30'
                              : action.suggestionType === 'compare_decide'
                              ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30'
                              : 'bg-amber-500/15 text-amber-400'
                          }`}>
                            {action.suggestionType === 'synthesize_archive'
                              ? 'Compound Workflow'
                              : action.suggestionType === 'compare_decide'
                              ? 'Decision Matrix'
                              : 'AI suggestion'}
                          </span>
                        )}
                        {action.tabCount && action.tabCount > 0 && (
                          <span className="px-2 py-0.5 rounded-full text-xs bg-khiimori-bg-elevated/20 text-khiimori-text-secondary">
                            {action.tabCount} tabs staged
                          </span>
                        )}
                      </div>
                      {action.description && (
                        <p className="text-sm text-khiimori-text-muted mb-1 line-clamp-2">{action.description}</p>
                      )}
                      <p className="text-sm text-khiimori-text-secondary mb-2">Trigger: {action.trigger}</p>
                      <div className="flex flex-wrap gap-2">
                        {action.actions.map((act, idx) => (
                          <span key={idx} className="px-2 py-1 bg-primary-500/20 text-primary-400 rounded text-xs">{act}</span>
                        ))}
                      </div>
                      {action.source === 'suggestion' && (
                        <button
                          type="button"
                          onClick={() => setExpandedId(expandedId === action.id ? null : action.id)}
                          className="mt-2 inline-flex items-center gap-1 text-xs text-khiimori-text-muted hover:text-khiimori-text-primary"
                        >
                          {expandedId === action.id ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                          {expandedId === action.id ? 'Hide why' : 'Why this suggestion?'}
                        </button>
                      )}
                      {expandedId === action.id && action.source === 'suggestion' && (
                        <div className="mt-2 rounded-lg border border-khiimori-border/40 bg-khiimori-bg-elevated/40 p-3 text-xs text-khiimori-text-secondary">
                          <p className="mb-1">
                            <span className="text-khiimori-text-muted">Why:</span> {action.description || 'AI suggested this based on your current tabs and recent activity.'}
                          </p>
                          <p>
                            <span className="text-khiimori-text-muted">Impact:</span> {action.triggerCount > 0
                              ? `You've used similar actions ${action.triggerCount} time${action.triggerCount === 1 ? '' : 's'} before.`
                              : 'New suggestion — no prior history yet.'}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-4 lg:justify-end shrink-0">
                    <div className="text-right text-sm">
                      <div className="font-semibold text-khiimori-text-primary">{action.triggerCount} triggers</div>
                      <div className="text-xs text-khiimori-text-muted">Last: {formatLastTriggered(action.lastTriggered)}</div>
                      <div className="mt-1">
                        <span className="text-xs text-khiimori-text-muted">Efficiency:</span>
                        <span className="ml-1 font-semibold text-success-500">{action.efficiency}%</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={executingId === action.id || action.status === 'paused'}
                        onClick={() => void (action.source === 'workflow' ? executeWorkflow(action) : executeSuggestion(action))}
                        className="px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white rounded-lg text-sm font-medium flex items-center gap-2"
                      >
                        {executingId === action.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Play className="h-4 w-4" />
                        )}
                        {action.actionLabel ?? 'Run'}
                      </button>

                      {action.source === 'workflow' ? (
                        <>
                          <button
                            type="button"
                            className="p-2 hover:bg-khiimori-bg-elevated rounded-lg"
                            title={action.status === 'active' ? 'Pause' : 'Resume'}
                            onClick={() => void toggleWorkflowStatus(action)}
                          >
                            {action.status === 'active' ? (
                              <Pause className="h-5 w-5 text-warning-400" />
                            ) : (
                              <Play className="h-5 w-5 text-success-500" />
                            )}
                          </button>
                          <button
                            type="button"
                            className="p-2 hover:bg-khiimori-bg-elevated rounded-lg"
                            title="Delete"
                            onClick={() => void deleteWorkflow(action)}
                          >
                            <Trash2 className="h-5 w-5 text-error-500" />
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="p-2 hover:bg-khiimori-bg-elevated rounded-lg"
                          title="Dismiss"
                          onClick={() => dismissSuggestion(action.id)}
                        >
                          <Trash2 className="h-5 w-5 text-khiimori-text-muted" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="mt-8 space-y-4">
        <Card className="bg-gradient-glass border-primary-400/30">
          <button
            type="button"
            onClick={() => void refresh()}
            className="w-full flex items-center space-x-4 text-left"
          >
            <div className="p-3 bg-primary-500 rounded-xl shrink-0">
              <Sparkles className="h-6 w-6 text-khiimori-text-primary" />
            </div>
            <div>
              <h3 className="font-bold text-khiimori-text-primary">AI Automation</h3>
              <p className="text-sm text-khiimori-text-secondary">Refresh AI suggestions based on your open tabs and patterns</p>
            </div>
          </button>
        </Card>

        {rules.length > 0 && (
          <Card>
            <h3 className="font-semibold text-khiimori-text-primary mb-4">Suggestion rules</h3>
            <ul className="space-y-3">
              {rules.map((rule) => (
                <li key={rule.id} className="flex items-center justify-between gap-4 py-2 border-b border-khiimori-border/40 last:border-0">
                  <div className="min-w-0">
                    <p className="font-medium text-khiimori-text-primary">{rule.name}</p>
                    <p className="text-xs text-khiimori-text-muted truncate">{rule.condition}</p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={rule.enabled}
                    onClick={() => void toggleRule(rule)}
                    className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${rule.enabled ? 'bg-primary-500' : 'bg-khiimori-bg-tertiary'}`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${rule.enabled ? 'translate-x-5' : ''}`} />
                  </button>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </motion.div>
    </PageLayout>
  );
};

export default SmartActionsDashboard;
